import React, { FC, useMemo, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { useJourneyApi } from '../journey-api-context';
import {
	JourneyFilterState,
	JourneySegment,
	TimeBetweenResult,
} from '../../../../models/journey-explorer/journey-explorer';
import { timeSummary } from '../journey-segments';
import { RequestLoader, useDebouncedRequest } from '../use-debounced-request';

const LOAD_ERROR = 'The time between these steps could not be loaded.';

interface TimeBetweenProps {
	filters: JourneyFilterState;
	segment: JourneySegment;
}

const TimeBetween: FC<TimeBetweenProps> = ({ filters, segment }) => {
	const apiService = useJourneyApi();
	const apiRef = useRef(apiService);
	apiRef.current = apiService;
	const [attempt, setAttempt] = useState(0);

	const load = useMemo<RequestLoader<TimeBetweenResult>>(
		() => (signal) => apiRef.current.getTimeBetween({ filters, segment }, signal),
		[filters, segment]
	);
	const { data, loading, error } = useDebouncedRequest(load, LOAD_ERROR, attempt);

	if (error) {
		return (
			<div className={styles.drawer_message}>
				{error}
				<button type="button" className={styles.retry} onClick={() => setAttempt((value) => value + 1)}>
					Retry
				</button>
			</div>
		);
	}
	if (!data) {
		return (
			<div className={styles.drawer_message}>
				<span className={styles.spinner} aria-label="Loading" />
			</div>
		);
	}

	const summary = timeSummary(data);
	if (!summary) {
		return <div className={styles.drawer_message}>No customers made this move with these filters.</div>;
	}
	const maxPct = Math.max(1, ...data.bins.map((bin) => bin.pct));

	return (
		<div className={`${styles.time} ${loading ? styles.loading : ''}`}>
			<p className={styles.time_sentence}>{summary.sentence}</p>
			{data.bins.map((bin) => (
				<div key={bin.label} className={styles.time_row}>
					<span className={styles.time_label}>{bin.label}</span>
					<span className={styles.time_bar}>
						<span style={{ width: `${(bin.pct / maxPct) * 100}%` }} />
					</span>
					<span className={styles.time_pct}>{bin.pct.toFixed(0)}%</span>
				</div>
			))}
			<p className={styles.time_advice}>{summary.advice}</p>
		</div>
	);
};

export default TimeBetween;
